/**
 * CCTV 기기 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Row, Col, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { cctvService } from '../../services/cctvService';
import { CctvDevice } from '../../types/cctv';
import { format } from 'date-fns';

const CctvDeviceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deviceId = id ? parseInt(id, 10) : 0;
  const isNew = id === 'new';

  const { data: device, isLoading } = useQuery({
    queryKey: ['cctvDevice', deviceId],
    queryFn: () => cctvService.getDevice(deviceId),
    enabled: !!deviceId && deviceId > 0 && !isNew,
  });

  const deleteMutation = useMutation({
    mutationFn: () => cctvService.deleteDevice(deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cctvDevices'] });
      navigate('/admin/cctv/devices');
    },
    onError: (error: any) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">활성</Badge>;
      case 'inactive':
        return <Badge bg="secondary">비활성</Badge>;
      case 'maintenance':
        return <Badge bg="warning">점검중</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleDelete = () => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      deleteMutation.mutate();
    }
  };

  if (isNew) {
    navigate('/admin/cctv/devices/new');
    return null;
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!device) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>CCTV 기기를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/cctv/devices')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>CCTV 기기 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/cctv/devices')} className="me-2">
              목록으로
            </Button>
            <Button variant="info" onClick={() => navigate(`/admin/cctv/devices/${deviceId}/monitor`)} className="me-2">
              실시간 모니터링
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/cctv/devices/${deviceId}/edit`)} className="me-2">
              수정
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              삭제
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <h3>{device.device_name}</h3>
                  <div className="mt-2">
                    {getStatusBadge(device.status)}
                  </div>
                </div>

                <div className="mb-3">
                  <strong>기기 코드:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>{device.device_code}</span>
                </div>

                {device.location && (
                  <div className="mb-3">
                    <strong>설치 위치:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>{device.location}</span>
                  </div>
                )}
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <strong>등록일:</strong> 
                  <span style={{ color: '#ccc', marginLeft: '10px' }}>
                    {format(new Date(device.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </span>
                </div>

                {device.updated_at && (
                  <div className="mb-3">
                    <strong>수정일:</strong> 
                    <span style={{ color: '#ccc', marginLeft: '10px' }}>
                      {format(new Date(device.updated_at), 'yyyy-MM-dd HH:mm:ss')}
                    </span>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="mb-3">스트리밍 정보</h5>
            {device.stream_url && (
              <div className="mb-3">
                <strong>스트림 URL:</strong> 
                <div style={{ 
                  color: '#00BCD4', 
                  marginTop: '5px',
                  padding: '10px',
                  backgroundColor: '#2E2E3A',
                  borderRadius: '5px',
                  wordBreak: 'break-all'
                }}>
                  {device.stream_url}
                </div>
              </div>
            )}

            {device.access_url && (
              <div className="mb-3">
                <strong>접속 URL:</strong> 
                <div style={{ 
                  color: '#00BCD4', 
                  marginTop: '5px',
                  padding: '10px',
                  backgroundColor: '#2E2E3A',
                  borderRadius: '5px',
                  wordBreak: 'break-all'
                }}>
                  {device.access_url}
                </div>
              </div>
            )}

            {!device.stream_url && !device.access_url && (
              <Alert variant="info">
                스트리밍 정보가 설정되지 않았습니다. 수정 페이지에서 설정해주세요.
              </Alert>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>실시간 모니터링</h5>
              <Button variant="info" size="sm" onClick={() => navigate(`/admin/cctv/devices/${deviceId}/monitor`)}>
                모니터링 시작
              </Button>
            </div>
            <Alert variant="info">
              CCTV 실시간 모니터링은 별도 페이지에서 제공됩니다. 스트리밍 URL이 설정되어 있어야 합니다.
            </Alert>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CctvDeviceDetail;

